import { useEffect, useRef, useState } from "react";

type HeroVideoProps = {
  src: string;
  poster?: string;
  label: string;
  className?: string;
};

/**
 * Autoplay-resilient hero video.
 *
 * Browsers reject muted autoplay in more cases than the spec suggests (data
 * saver, low-power mode, background tabs, some in-app webviews). We therefore
 * drive play() ourselves, retry when the tab becomes visible or the element
 * scrolls into view, and fall back to a tap-to-play affordance over the poster
 * frame so the slot is never a blank rectangle.
 */
export function HeroVideo({ src, poster, label, className }: HeroVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let cancelled = false;
    setBlocked(false);
    el.load();

    const attempt = () => {
      if (cancelled || !el.paused) return;
      el.muted = true;
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.then(() => !cancelled && setBlocked(false)).catch(() => {
          if (!cancelled) setBlocked(true);
        });
      }
    };

    attempt();

    el.addEventListener("loadeddata", attempt);
    el.addEventListener("canplay", attempt);
    document.addEventListener("visibilitychange", attempt);
    window.addEventListener("pointerdown", attempt, { once: true });

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && attempt()),
      { threshold: 0.1 }
    );
    io.observe(el);

    return () => {
      cancelled = true;
      el.removeEventListener("loadeddata", attempt);
      el.removeEventListener("canplay", attempt);
      document.removeEventListener("visibilitychange", attempt);
      window.removeEventListener("pointerdown", attempt);
      io.disconnect();
    };
  }, [src]);

  return (
    <>
      <video
        key={src}
        ref={ref}
        src={src || undefined}
        poster={poster}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        aria-label={label}
        className={className}
      />
      {blocked && (
        <button
          type="button"
          onClick={() => ref.current?.play().then(() => setBlocked(false)).catch(() => {})}
          aria-label="Play the Cardmoji reel"
          className="absolute inset-0 flex items-center justify-center bg-ink/20"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-paper shadow-lg">
            <svg viewBox="0 0 24 24" className="ml-1 h-6 w-6 fill-ink" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      )}
    </>
  );
}
